<script setup>
// M26.5 — AI Workforce Identity Center, built on the M26.2.1 runtime work:
//   GET nexceo/ai_workforce        (real employees + conceptual registry)
//   GET nexceo/workforce_tasks     (account-wide task queue — currently
//                                    empty in production; WorkforceTaskQueue
//                                    has no live callers yet, see the
//                                    M26.2.1 audit. Shown honestly as-is.)
//   GET nexceo/workforce_analytics (account-wide aggregates)
//   GET nexceo/workforce_activity  (Nexceo::Ai::WorkforceActivityService —
//                                    real per-project/per-role KPIs +
//                                    knowledge/memory/response-time
//                                    (extended for M26.5) + the account-wide
//                                    historical event timeline)
// No polling this round (M26.5 Part 13) — one load on mount, manual refresh.
// Every card field is real-or-— (workforceFormat.js), never a fabricated 0
// (M26.5 Part 5) except where a count is genuinely, definitively zero.
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import aiWorkforceAPI from 'dashboard/api/nexceo/aiWorkforce';
import workforceTasksAPI from 'dashboard/api/nexceo/workforceTasks';
import workforceAnalyticsAPI from 'dashboard/api/nexceo/workforceAnalytics';
import workforceActivityAPI from 'dashboard/api/nexceo/workforceActivity';
import nexceoSettingsAPI from 'dashboard/api/nexceo/settings';
import OrchestrationFlow from './workforce/OrchestrationFlow.vue';
import WorkforceMetricsStrip from './workforce/WorkforceMetricsStrip.vue';
import WorkforceEmployeeCard from './workforce/WorkforceEmployeeCard.vue';
import AiProfileDrawer from './workforce/AiProfileDrawer.vue';
import TaskQueuePanel from './workforce/TaskQueuePanel.vue';
import EventTimelinePanel from './workforce/EventTimelinePanel.vue';
import CapabilityMatrix from './workforce/CapabilityMatrix.vue';
import WorkforceHealthPanel from './workforce/WorkforceHealthPanel.vue';
import LiveActivityStrip from './workforce/LiveActivityStrip.vue';
import WorkforceCardSkeleton from './workforce/WorkforceCardSkeleton.vue';
import { departmentColor } from './workforce/departmentColors';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const hasLoadedOnce = ref(false);
const loadFailed = ref(false);
const error = ref('');
const employees = ref([]);
const globalEnabled = ref(true);
const registry = ref([]);
const tasks = ref([]);
const analytics = ref(null);
const activity = ref(null);
const companyAi = reactive({
  enabled: true,
  display_name: 'ZOYA',
  role_title: 'AI Sales Coordinator',
});
const togglingId = ref(null);
const togglingGlobal = ref(false);
const searchQuery = ref('');
const filterDepartment = ref('');
const filterProject = ref('');
const filterStatus = ref('');
const filterHealth = ref('');
const selectedUnitKey = ref(null);
let errorTimer = null;

function showError(msg) {
  error.value = msg;
  clearTimeout(errorTimer);
  errorTimer = setTimeout(() => {
    error.value = '';
  }, 5000);
}

async function loadData() {
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const [workforceRes, settingsRes, tasksRes, analyticsRes, activityRes] =
      await Promise.all([
        aiWorkforceAPI.get(),
        nexceoSettingsAPI.get(),
        workforceTasksAPI.get({ per_page: 100 }),
        workforceAnalyticsAPI.get(todayStart.toISOString()),
        workforceActivityAPI.get(),
      ]);
    employees.value = workforceRes.data.ai_employees || [];
    globalEnabled.value = workforceRes.data.global_ai_enabled ?? true;
    registry.value = workforceRes.data.workforce_registry || [];
    const s = settingsRes.data.settings || {};
    companyAi.enabled = s.company_ai_enabled ?? true;
    companyAi.display_name = s.company_ai_display_name || 'ZOYA';
    companyAi.role_title = s.company_ai_role_title || 'AI Sales Coordinator';
    tasks.value = tasksRes.data.tasks || [];
    analytics.value = analyticsRes.data || null;
    activity.value = activityRes.data || null;
    hasLoadedOnce.value = true;
  } catch (e) {
    loadFailed.value = true;
    const status = e?.response?.status;
    showError(
      status
        ? `Failed to load workforce data. (HTTP ${status})`
        : 'Failed to load workforce data. Check your connection.'
    );
  } finally {
    isLoading.value = false;
  }
}

// ── Icon catalogue per registry role key (color now comes from
// departmentColors.js — see GeneratedAvatar/WorkforceEmployeeCard) ────────
const ROLE_ICON = {
  company_coordinator: 'i-lucide-globe',
  sales_manager: 'i-lucide-handshake',
  project: 'i-lucide-building-2',
  crm: 'i-lucide-database',
  documentation: 'i-lucide-file-text',
  calendar: 'i-lucide-calendar',
  marketing: 'i-lucide-megaphone',
  finance: 'i-lucide-indian-rupee',
  legal: 'i-lucide-scale',
  support: 'i-lucide-life-buoy',
  nexa: 'i-lucide-users',
};

function registryRole(key) {
  return registry.value.find(r => r.id === key || r.role === key) || null;
}

// ── Role-specific numbers that don't fit the fixed Leads/Replies/
// Documents/Visits/Queue/Active set (M26.5 Part 4) — shown as small extra
// chips so nothing real gets dropped. Real data only, per role_kpis.
const EXTRA_KPI_LABELS = {
  qualified_leads: 'Qualified',
  transferred_to_project: 'Transferred',
  avg_qualification_hours: 'Avg Qualify (hrs)',
  negotiations_started: 'Negotiations',
  converted_leads: 'Converted',
  lead_updates: 'Lead Updates',
  kanban_moves: 'Kanban Moves',
  pending_approvals: 'Pending Approval',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  reminders_sent: 'Reminders Sent',
  legal_documents_shared: 'Legal Docs',
  escalations: 'Escalations',
  active_staff_users: 'Active Staff',
};

function extraKpisFor(roleKey, exclude = []) {
  const kpi = activity.value?.role_kpis?.[roleKey] || {};
  return Object.entries(kpi)
    .filter(
      ([key, value]) =>
        !exclude.includes(key) && value != null && EXTRA_KPI_LABELS[key]
    )
    .map(([key, value]) => ({ label: EXTRA_KPI_LABELS[key], value }));
}

// Registry roles whose KPI vocabulary doesn't use the card's fixed
// documentsShared/siteVisits names directly (documentation reports
// brochures/videos/floor-plans separately; calendar reports
// scheduled/completed separately) — real sums only, no other role gets a
// value here.
function documentsSharedFor(roleId, kpi) {
  if (roleId === 'documentation') {
    return (
      (kpi.brochures_shared || 0) +
      (kpi.videos_shared || 0) +
      (kpi.floor_plans_shared || 0)
    );
  }
  if (roleId === 'legal') return kpi.legal_documents_shared ?? null;
  return null;
}
function siteVisitsFor(roleId, kpi) {
  return roleId === 'calendar'
    ? (kpi.scheduled || 0) + (kpi.completed || 0)
    : null;
}

function healthForStatus(status) {
  if (status === 'active') return 'healthy';
  if (status === 'unavailable' || status === 'unknown') return 'attention';
  return 'degraded'; // paused / disabled — intentional, but worth surfacing
}

// Real (possibly zero) queue/active counts for a unit, matched against the
// account-wide workforce_tasks list already fetched — never fabricated,
// but a genuine 0 when nothing matches (Part 5: 0 is fine when it's real).
function taskCounts(matchFn) {
  const matched = tasks.value.filter(matchFn);
  return {
    queueCount: matched.filter(t => t.status === 'pending').length,
    activeCount: matched.filter(t => t.status === 'running').length,
  };
}

// M26.5.1 Part 8 — premium 6-state status, derived only from fields already
// present on the unit (never new data). Priority order: offline/paused are
// definitive; a running task or in-flight toggle means the AI is actively
// processing; a reply in the last 5 minutes reads as live; a non-empty queue
// means it's waiting on capacity; a non-empty Currently Working On list with
// no other signal reads as busy; otherwise it's live and idle.
const LIVE_RECENCY_MS = 5 * 60 * 1000;

function deriveLiveStatus(base) {
  if (['disabled', 'unavailable', 'unknown'].includes(base.status)) {
    return 'OFFLINE';
  }
  if (base.status === 'paused') return 'PAUSED';
  if ((base.activeCount || 0) > 0 || base.busy) return 'PROCESSING';
  if (
    base.lastActivity?.at &&
    Date.now() - new Date(base.lastActivity.at).getTime() < LIVE_RECENCY_MS
  ) {
    return 'LIVE';
  }
  if ((base.queueCount || 0) > 0) return 'WAITING';
  if (base.currentlyWorkingOn?.length) return 'BUSY';
  return 'LIVE';
}

function buildUnit(base) {
  return {
    ...base,
    health: healthForStatus(base.status),
    liveStatus: deriveLiveStatus(base),
  };
}

// ── Normalize every workforce entity into one card shape ──────────────────
const units = computed(() => {
  const list = [];

  const coordinatorRole = registryRole('company_coordinator');
  if (coordinatorRole) {
    const kpi = activity.value?.role_kpis?.company_coordinator || {};
    list.push(
      buildUnit({
        key: 'company_coordinator',
        kind: 'coordinator',
        name: companyAi.display_name,
        poweredBy: null,
        roleLabel: companyAi.role_title,
        department: 'company_coordinator',
        projectName: null,
        icon: ROLE_ICON.company_coordinator,
        status: companyAi.enabled ? 'active' : 'disabled',
        capabilities: coordinatorRole.capabilities || [],
        busy: false,
        leadsManaged: kpi.incoming_leads ?? null,
        aiReplies: null,
        documentsShared: null,
        siteVisits: null,
        knowledgePercent: undefined,
        memoryPercent: undefined,
        avgResponseMs: null,
        workloadPct: null,
        ...taskCounts(t =>
          coordinatorRole.capabilities?.includes(t.capability)
        ),
        extraKpis: extraKpisFor('company_coordinator', ['incoming_leads']),
        lastActivity: null,
        latestLeadId: null,
        currentlyWorkingOn: [],
        quickActionLabel: 'Configure',
        quickActionIcon: 'i-lucide-settings',
        quickActionType: 'navigate',
        quickActionTo: accountScopedRoute('nexceo_company_ai'),
      })
    );
  }

  employees.value.forEach(emp => {
    const projectKpi = activity.value?.project_kpis?.[emp.project_id] || {};
    const projectName = emp.project?.name;
    list.push(
      buildUnit({
        key: `project-${emp.id}`,
        kind: 'project',
        // M26.5 Part 2 — project is the primary identity, the human/model
        // employee is secondary ("Powered by"), never the reverse.
        name: projectName ? `${projectName} AI` : emp.name,
        poweredBy: projectName ? emp.name : null,
        roleLabel: emp.role_label || emp.role,
        department: 'project',
        projectName,
        projectId: emp.project_id,
        icon: ROLE_ICON.project,
        status: emp.status,
        capabilities: emp.allowed_tools || [],
        busy: togglingId.value === emp.id,
        leadsManaged: projectKpi.leads_managed ?? 0,
        aiReplies: projectKpi.ai_replies ?? 0,
        documentsShared: projectKpi.documents_shared ?? 0,
        siteVisits: projectKpi.site_visits_scheduled ?? 0,
        knowledgePercent: projectKpi.knowledge_completion_percent ?? null,
        memoryPercent: projectKpi.memory_completion_percent ?? null,
        avgResponseMs: projectKpi.avg_response_ms ?? null,
        workloadPct: null,
        ...taskCounts(t => t.project_id === emp.project_id),
        extraKpis: [],
        lastActivity: projectKpi.last_customer_reply
          ? { label: 'Last customer reply', at: projectKpi.last_customer_reply }
          : null,
        latestLeadId: projectKpi.latest_lead_id ?? null,
        currentlyWorkingOn: projectKpi.active_leads ?? [],
        quickActionLabel: emp.status === 'active' ? 'Pause' : 'Activate',
        quickActionIcon:
          emp.status === 'active' ? 'i-lucide-pause' : 'i-lucide-play',
        quickActionType: 'toggle',
        employeeId: emp.id,
      })
    );
  });

  const nexaRole = registryRole('nexa');
  if (nexaRole) {
    list.push(
      buildUnit({
        key: 'nexa',
        kind: 'staff',
        name: nexaRole.name,
        poweredBy: null,
        roleLabel: nexaRole.title,
        department: 'nexa',
        projectName: null,
        icon: ROLE_ICON.nexa,
        status: nexaRole.status,
        capabilities: nexaRole.capabilities || [],
        busy: false,
        leadsManaged: null,
        aiReplies: null,
        documentsShared: null,
        siteVisits: null,
        knowledgePercent: undefined,
        memoryPercent: undefined,
        avgResponseMs: null,
        workloadPct: null,
        ...taskCounts(t => nexaRole.capabilities?.includes(t.capability)),
        extraKpis: extraKpisFor('nexa'),
        lastActivity: null,
        latestLeadId: null,
        currentlyWorkingOn: [],
        quickActionLabel: 'Open',
        quickActionIcon: 'i-lucide-arrow-right',
        quickActionType: 'navigate',
        quickActionTo: accountScopedRoute('nexceo_staff_ai'),
      })
    );
  }

  registry.value
    .filter(r => !['company_coordinator', 'project', 'nexa'].includes(r.id))
    .forEach(role => {
      const kpi = activity.value?.role_kpis?.[role.id] || {};
      const documentsShared = documentsSharedFor(role.id, kpi);
      const siteVisits = siteVisitsFor(role.id, kpi);

      list.push(
        buildUnit({
          key: `registry-${role.id}`,
          kind: 'registry',
          name: role.name,
          poweredBy: null,
          roleLabel: role.title,
          department: role.id,
          projectName: null,
          icon: ROLE_ICON[role.id] || 'i-lucide-bot',
          status: role.status,
          capabilities: role.capabilities || [],
          busy: false,
          leadsManaged: null,
          aiReplies: null,
          documentsShared,
          siteVisits,
          knowledgePercent: undefined,
          memoryPercent: undefined,
          avgResponseMs: null,
          workloadPct: null,
          ...taskCounts(t => role.capabilities?.includes(t.capability)),
          extraKpis: extraKpisFor(role.id, [
            'brochures_shared',
            'videos_shared',
            'floor_plans_shared',
            'scheduled',
            'completed',
            'legal_documents_shared',
          ]),
          lastActivity: null,
          latestLeadId: null,
          currentlyWorkingOn: [],
          quickActionLabel: 'View tasks',
          quickActionIcon: 'i-lucide-list-todo',
          quickActionType: 'navigate',
          quickActionTo: accountScopedRoute('nexceo_workforce_tasks'),
        })
      );
    });

  return list;
});

const selectedUnit = computed(
  () => units.value.find(u => u.key === selectedUnitKey.value) || null
);
const projectUnits = computed(() =>
  units.value.filter(u => u.kind === 'project')
);

// M26.5.1 Part 4 — client-side filters over the already-loaded units, no
// backend change. Option lists are derived from real data present, never a
// hardcoded vocabulary — a filter never offers a choice with zero matches.
function uniqueOptions(mapFn) {
  const seen = new Map();
  units.value.forEach(u => {
    const entry = mapFn(u);
    if (entry && !seen.has(entry.value)) seen.set(entry.value, entry.label);
  });
  return Array.from(seen, ([value, label]) => ({ value, label }));
}

const departmentOptions = computed(() =>
  uniqueOptions(u => ({
    value: u.department,
    label: departmentColor(u.department).label,
  }))
);
const projectOptions = computed(() =>
  uniqueOptions(u =>
    u.projectName ? { value: u.projectName, label: u.projectName } : null
  )
);
const statusOptions = computed(() =>
  uniqueOptions(u => (u.status ? { value: u.status, label: u.status } : null))
);
const healthOptions = computed(() =>
  uniqueOptions(u => (u.health ? { value: u.health, label: u.health } : null))
);

const filteredUnits = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return units.value.filter(u => {
    if (q) {
      const matchesQuery =
        u.name.toLowerCase().includes(q) ||
        u.roleLabel?.toLowerCase().includes(q) ||
        u.projectName?.toLowerCase().includes(q);
      if (!matchesQuery) return false;
    }
    if (filterDepartment.value && u.department !== filterDepartment.value)
      return false;
    if (filterProject.value && u.projectName !== filterProject.value)
      return false;
    if (filterStatus.value && u.status !== filterStatus.value) return false;
    if (filterHealth.value && u.health !== filterHealth.value) return false;
    return true;
  });
});

function clearFilters() {
  searchQuery.value = '';
  filterDepartment.value = '';
  filterProject.value = '';
  filterStatus.value = '';
  filterHealth.value = '';
}

const hasActiveFilters = computed(
  () =>
    !!(
      searchQuery.value ||
      filterDepartment.value ||
      filterProject.value ||
      filterStatus.value ||
      filterHealth.value
    )
);

function registryKpiCount(roleKey, fields) {
  const kpi = activity.value?.role_kpis?.[roleKey];
  if (!kpi) return 0;
  return fields.reduce((sum, f) => sum + (kpi[f] || 0), 0);
}

// ── Orchestration flow nodes (counts from real role/project KPIs) ─────────
const orchestrationNodes = computed(() => {
  const zoya = units.value.find(u => u.key === 'company_coordinator');
  const sales = registryKpiCount('sales_manager', ['negotiations_started']);
  const documentation = registryKpiCount('documentation', [
    'brochures_shared',
    'videos_shared',
    'floor_plans_shared',
  ]);
  const calendar = registryKpiCount('calendar', ['scheduled', 'completed']);
  const projectActive = employees.value.some(e => e.status === 'active');
  const projectLeads = units.value
    .filter(u => u.kind === 'project')
    .reduce(
      (sum, u) =>
        sum + (activity.value?.project_kpis?.[u.projectId]?.leads_managed || 0),
      0
    );

  return [
    { key: 'customer', label: 'Customer', icon: 'i-lucide-user', active: true },
    {
      key: 'zoya',
      label: 'ZOYA',
      icon: 'i-lucide-globe',
      active: zoya?.status === 'active',
      count:
        activity.value?.role_kpis?.company_coordinator?.incoming_leads || 0,
    },
    {
      key: 'sales',
      label: 'Sales Manager',
      icon: 'i-lucide-handshake',
      active: sales > 0,
      count: sales,
    },
    {
      key: 'project',
      label: 'Project Layer',
      icon: 'i-lucide-building-2',
      active: projectActive,
      count: projectLeads,
    },
    {
      key: 'documentation',
      label: 'Documentation',
      icon: 'i-lucide-file-text',
      active: documentation > 0,
      count: documentation,
    },
    {
      key: 'calendar',
      label: 'Calendar',
      icon: 'i-lucide-calendar',
      active: calendar > 0,
      count: calendar,
    },
    {
      key: 'human',
      label: 'Human',
      icon: 'i-lucide-user-check',
      active: (activity.value?.role_kpis?.support?.escalations || 0) > 0,
      count: activity.value?.role_kpis?.support?.escalations || 0,
    },
  ];
});

// ── Metrics strip ──────────────────────────────────────────────────────
const metrics = computed(() => {
  const runningCount = tasks.value.filter(t => t.status === 'running').length;
  const waitingCount = tasks.value.filter(t => t.status === 'pending').length;
  const activeEmployees = units.value.filter(u => u.status === 'active').length;
  const workCompleted = analytics.value?.work_completed || 0;
  const workFailed = analytics.value?.work_failed || 0;
  const successRate =
    workCompleted + workFailed > 0
      ? Math.round((workCompleted / (workCompleted + workFailed)) * 100)
      : 100;
  const avgMs = analytics.value?.average_response_ms;
  const w = activity.value?.workload;
  // Part 8: today's activity + pending approvals + open/running tasks,
  // scaled onto a 0-100 gauge — real counts, not a fabricated placeholder.
  const workloadRaw = w
    ? w.today_activity_count +
      w.pending_activity_count +
      w.open_task_count +
      w.running_task_count
    : 0;
  const workloadPct = Math.min(100, workloadRaw * 4);

  return [
    {
      key: 'active',
      label: 'Active AI Employees',
      value: activeEmployees,
      icon: 'i-lucide-bot',
      color: '#6366f1',
    },
    {
      key: 'running',
      label: 'Running Tasks',
      value: runningCount,
      icon: 'i-lucide-loader-2',
      color: '#0ea5e9',
    },
    {
      key: 'waiting',
      label: 'Waiting Queue',
      value: waitingCount,
      icon: 'i-lucide-clock',
      color: '#f59e0b',
    },
    {
      key: 'today',
      label: "Today's AI Activity",
      value: workCompleted,
      icon: 'i-lucide-message-circle',
      color: '#10b981',
      hint: 'Completed AI work items today — closest available proxy for conversations touched.',
    },
    {
      key: 'response',
      label: 'Avg Response Time',
      value: avgMs ? `${(avgMs / 1000).toFixed(1)}s` : '—',
      icon: 'i-lucide-zap',
      color: '#f97316',
    },
    {
      key: 'success',
      label: 'Success Rate',
      value: `${successRate}%`,
      icon: 'i-lucide-check-circle-2',
      color: '#22c55e',
    },
    {
      key: 'workload',
      label: 'Workforce Workload',
      value: `${workloadPct}%`,
      icon: 'i-lucide-gauge',
      color: '#8b5cf6',
      hint: "Today's activity + pending approvals + open/running tasks, scaled to a 0-100 gauge.",
    },
  ];
});

// ── Task queue panel — the real (currently empty) WorkforceTask queue,
// plus a real category breakdown of the last 24h of actual activity so the
// panel isn't just a permanently-blank list (Part 7: no fake counters, but
// don't hide genuine runtime activity either). ─────────────────────────────
const queuedTasks = computed(() =>
  tasks.value
    .filter(t => t.status === 'pending' || t.status === 'running')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8)
);

const categoryCounts = computed(() => {
  const events = activity.value?.events || [];
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
  const recent = events.filter(e => new Date(e.at).getTime() >= dayAgo);
  const countType = type => recent.filter(e => e.type === type).length;
  return [
    {
      label: 'Conversation Tasks',
      value: countType('ai_replied'),
      icon: 'i-lucide-message-circle',
    },
    {
      label: 'Document Tasks',
      value: countType('document_shared'),
      icon: 'i-lucide-file-text',
    },
    {
      label: 'Meeting Tasks',
      value:
        countType('site_visit_scheduled') + countType('site_visit_completed'),
      icon: 'i-lucide-calendar',
    },
  ].filter(c => c.value > 0);
});

// ── Event timeline — real account-wide LeadActivity history ────────────────
const EVENT_META = {
  ai_replied: {
    icon: 'i-lucide-message-circle',
    colorClass:
      'bg-woot-100 text-woot-700 dark:bg-woot-500/20 dark:text-woot-400',
  },
  document_shared: {
    icon: 'i-lucide-file-text',
    colorClass:
      'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
  },
  site_visit_scheduled: {
    icon: 'i-lucide-calendar-plus',
    colorClass:
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400',
  },
  site_visit_completed: {
    icon: 'i-lucide-calendar-check',
    colorClass:
      'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400',
  },
  site_visit_cancelled: {
    icon: 'i-lucide-calendar-x',
    colorClass:
      'bg-ruby-100 text-ruby-700 dark:bg-ruby-500/20 dark:text-ruby-400',
  },
  human_handoff: {
    icon: 'i-lucide-user-check',
    colorClass:
      'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
  },
  stage_changed: {
    icon: 'i-lucide-move-right',
    colorClass: 'bg-n-alpha-2 text-n-slate-8',
  },
  marked_booked: {
    icon: 'i-lucide-party-popper',
    colorClass:
      'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400',
  },
  marked_lost: {
    icon: 'i-lucide-x-circle',
    colorClass:
      'bg-ruby-100 text-ruby-700 dark:bg-ruby-500/20 dark:text-ruby-400',
  },
  agent_assigned: {
    icon: 'i-lucide-user-plus',
    colorClass:
      'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  },
  action_blocked: {
    icon: 'i-lucide-shield-alert',
    colorClass:
      'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
  },
};
const DEFAULT_EVENT_META = {
  icon: 'i-lucide-circle',
  colorClass: 'bg-n-alpha-2 text-n-slate-8',
};

const timelineEvents = computed(() =>
  (activity.value?.events || []).slice(0, 25).map(e => ({
    id: e.id,
    type: e.type,
    ...(EVENT_META[e.type] || DEFAULT_EVENT_META),
    title: e.label,
    detail:
      [e.lead_name, e.project_name].filter(Boolean).join(' · ') || 'Unassigned',
    at: e.at,
  }))
);

// ── Health rollup ──────────────────────────────────────────────────────
const healthSummary = computed(() => {
  const summary = { healthy: 0, degraded: 0, attention: 0 };
  units.value.forEach(u => {
    summary[u.health] = (summary[u.health] || 0) + 1;
  });
  return summary;
});

// M26.5 Part 9 — Knowledge/Memory/Health/Latency/Queue/Failures/Response
// Time/Success Rate, each averaged/counted only from real values already on
// the page; null (→ —) when nothing real backs it, never a fabricated 0.
function average(values) {
  const real = values.filter(v => v !== null && v !== undefined);
  return real.length
    ? Math.round(real.reduce((sum, v) => sum + v, 0) / real.length)
    : null;
}

const healthStats = computed(() => {
  const w = activity.value?.workload;
  const workCompleted = analytics.value?.work_completed || 0;
  const workFailed = analytics.value?.work_failed || 0;
  const healthyCount = units.value.filter(u => u.health === 'healthy').length;

  return {
    knowledgePercent: average(projectUnits.value.map(u => u.knowledgePercent)),
    memoryPercent: average(projectUnits.value.map(u => u.memoryPercent)),
    healthPercent: units.value.length
      ? Math.round((healthyCount / units.value.length) * 100)
      : null,
    latencyMs: analytics.value?.average_response_ms ?? null,
    queueCount: w ? w.open_task_count : null,
    failuresCount: w ? w.failed_task_count : null,
    responseMs: average(projectUnits.value.map(u => u.avgResponseMs)),
    successRate:
      workCompleted + workFailed > 0
        ? Math.round((workCompleted / (workCompleted + workFailed)) * 100)
        : null,
  };
});

async function onToggleGlobal() {
  togglingGlobal.value = true;
  try {
    const res = await aiWorkforceAPI.toggleGlobal(!globalEnabled.value);
    globalEnabled.value = res.data.global_ai_enabled;
  } catch {
    showError('Failed to update global AI setting.');
  } finally {
    togglingGlobal.value = false;
  }
}

async function onToggleEmployee(employeeId, name) {
  togglingId.value = employeeId;
  try {
    const res = await aiWorkforceAPI.toggle(employeeId);
    const idx = employees.value.findIndex(e => e.id === employeeId);
    if (idx !== -1) employees.value[idx] = res.data;
  } catch {
    showError(`Failed to toggle ${name}.`);
  } finally {
    togglingId.value = null;
  }
}

function onQuickAction(unit) {
  if (unit.quickActionType === 'toggle') {
    onToggleEmployee(unit.employeeId, unit.name);
  } else if (unit.quickActionTo) {
    router.push(unit.quickActionTo);
  }
}

function openDrawer(unit) {
  selectedUnitKey.value = unit.key;
}
function closeDrawer() {
  selectedUnitKey.value = null;
}

const tasksViewAllTo = computed(() =>
  accountScopedRoute('nexceo_workforce_tasks')
);

function goToAnalytics() {
  router.push(accountScopedRoute('nexceo_workforce_analytics'));
}

function goToCertification() {
  router.push(accountScopedRoute('nexceo_ai_core_health'));
}

onMounted(loadData);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-2 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-network size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">
        AI Workforce Overview
      </h1>

      <div class="ml-auto flex items-center gap-2">
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
          title="Refresh"
          :disabled="isLoading"
          @click="loadData"
        >
          <span
            class="i-lucide-refresh-cw size-3.5"
            :class="{ 'animate-spin': isLoading }"
          />
        </button>
        <button
          class="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
          @click="goToAnalytics"
        >
          <span class="i-lucide-bar-chart-3 size-3.5" />
          Analytics
        </button>
        <button
          class="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
          @click="goToCertification"
        >
          <span class="i-lucide-shield-check size-3.5" />
          Certification
        </button>

        <div class="relative hidden sm:block">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search…"
            class="pl-8 pr-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-woot-500 w-40 transition-all"
          />
        </div>

        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors"
          :class="
            globalEnabled
              ? 'bg-woot-500 text-white hover:bg-woot-600'
              : 'border border-n-weak text-n-slate-9 hover:bg-n-alpha-2'
          "
          :disabled="togglingGlobal"
          @click="onToggleGlobal"
        >
          <span
            :class="
              togglingGlobal
                ? 'animate-spin i-lucide-loader-2'
                : globalEnabled
                  ? 'i-lucide-bot'
                  : 'i-lucide-bot-off'
            "
            class="size-3.5"
          />
          {{ globalEnabled ? 'AI Active' : 'AI Paused' }}
        </button>
      </div>
    </div>

    <!-- Global pause banner -->
    <div
      v-if="!globalEnabled"
      class="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-500/20 text-amber-800 dark:text-amber-400 text-sm shrink-0"
    >
      <span class="i-lucide-pause-circle size-4 shrink-0" />
      AI is globally paused. No AI employee will reply until re-activated.
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 dark:border-ruby-500/20 text-ruby-700 dark:text-ruby-400 text-sm shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ error }}
      <button
        class="ml-auto i-lucide-x size-4 opacity-60 hover:opacity-100"
        @click="error = ''"
      />
    </div>

    <!-- Loading -->
    <!-- First-load skeleton (M26.5.1 Part 9) — a background refresh (isLoading
         with data already on screen) keeps the real content mounted instead,
         so clicking Refresh no longer flashes the whole page away. -->
    <div
      v-if="isLoading && !hasLoadedOnce"
      class="flex-1 overflow-y-auto px-4 py-4"
    >
      <div class="max-w-7xl mx-auto flex flex-col gap-4">
        <div class="h-24 rounded-xl bg-n-alpha-2 animate-pulse" />
        <div class="h-16 rounded-xl bg-n-alpha-2 animate-pulse" />
        <WorkforceCardSkeleton />
      </div>
    </div>

    <!-- Content -->
    <div v-else class="flex-1 overflow-y-auto px-4 py-4">
      <div class="max-w-7xl mx-auto flex flex-col gap-4">
        <OrchestrationFlow
          :nodes="orchestrationNodes"
          :project-units="projectUnits"
        />

        <WorkforceMetricsStrip :metrics="metrics" />

        <LiveActivityStrip :events="timelineEvents.slice(0, 5)" />

        <!-- Orchestration cards -->
        <div>
          <div class="flex items-center gap-2 flex-wrap mb-3">
            <h2
              class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider flex items-center gap-1.5 shrink-0"
            >
              <span class="i-lucide-users size-3.5" />
              Workforce
              <span
                v-if="filteredUnits.length"
                class="text-[11px] px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-8 normal-case font-normal"
              >
                {{ filteredUnits.length }}
              </span>
            </h2>

            <div
              class="flex items-center gap-1.5 ml-auto flex-wrap w-full sm:w-auto justify-end"
            >
              <select
                v-model="filterDepartment"
                class="text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-11 py-1.5 px-2 outline-none focus:border-woot-500 flex-1 min-w-[130px] sm:flex-none sm:w-auto"
              >
                <option value="">All Departments</option>
                <option
                  v-for="opt in departmentOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <select
                v-if="projectOptions.length"
                v-model="filterProject"
                class="text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-11 py-1.5 px-2 outline-none focus:border-woot-500 flex-1 min-w-[130px] sm:flex-none sm:w-auto"
              >
                <option value="">All Projects</option>
                <option
                  v-for="opt in projectOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <select
                v-model="filterStatus"
                class="text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-11 py-1.5 px-2 outline-none focus:border-woot-500 capitalize flex-1 min-w-[130px] sm:flex-none sm:w-auto"
              >
                <option value="">All Statuses</option>
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                  class="capitalize"
                >
                  {{ opt.label }}
                </option>
              </select>
              <select
                v-model="filterHealth"
                class="text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-11 py-1.5 px-2 outline-none focus:border-woot-500 capitalize flex-1 min-w-[130px] sm:flex-none sm:w-auto"
              >
                <option value="">All Health</option>
                <option
                  v-for="opt in healthOptions"
                  :key="opt.value"
                  :value="opt.value"
                  class="capitalize"
                >
                  {{ opt.label }}
                </option>
              </select>
              <button
                v-if="hasActiveFilters"
                class="flex items-center gap-1 text-xs text-n-slate-8 hover:text-n-slate-12 px-2 py-1.5 shrink-0"
                @click="clearFilters"
              >
                <span class="i-lucide-x size-3" />
                Clear
              </button>
            </div>
          </div>
          <div
            v-if="filteredUnits.length === 0"
            class="text-center py-8 text-sm text-n-slate-7"
          >
            No matches found.
          </div>
          <TransitionGroup
            v-else
            name="card-pop"
            tag="div"
            class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
          >
            <WorkforceEmployeeCard
              v-for="(unit, index) in filteredUnits"
              :key="unit.key"
              :unit="unit"
              :style="{ '--card-i': index % 8 }"
              @quick-action="onQuickAction"
              @open="openDrawer"
            />
          </TransitionGroup>
        </div>

        <AiProfileDrawer
          :unit="selectedUnit"
          :is-open="!!selectedUnit"
          :events="activity?.events || []"
          @close="closeDrawer"
        />

        <!-- Queue / Timeline — Timeline gets more weight since the account-wide
             task queue has no live callers yet and is near-always empty. -->
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <TaskQueuePanel
            class="lg:col-span-2"
            :tasks="queuedTasks"
            :category-counts="categoryCounts"
            :view-all-to="tasksViewAllTo"
          />
          <EventTimelinePanel class="lg:col-span-3" :events="timelineEvents" />
        </div>

        <!-- Matrix / Health -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CapabilityMatrix
            :roles="registry"
            :capability-usage="analytics?.capability_usage || {}"
          />
          <WorkforceHealthPanel
            :summary="healthSummary"
            :units="units"
            :stats="healthStats"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* M26.5.1 Part 9 — staggered card entrance. --card-i is set per-card via
   inline style in the v-for above; each card's transition-delay scales off
   it so the grid populates in a soft cascade instead of popping in at once. */
.card-pop-enter-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  transition-delay: calc(var(--card-i, 0) * 40ms);
}
.card-pop-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.card-pop-move {
  transition: transform 0.3s ease;
}
</style>
